import React from 'react';

export interface RecommendedProduct {
  id: string;
  name: unknown;
  url: unknown;
  description?: string;
  images: Array<{ url: string }>;
  price: unknown;
  compare_at_price: unknown;
  currency_code: unknown;
  currency_display: unknown;
  variant_dimensions?: string[];
  variant_combinations?: Array<{
    values: string[];
    sku?: string;
    url?: string | null;
    price?: unknown;
    compare_at_price?: unknown;
    image_ids?: string[];
  }>;
  picked_variant_values?: string[];
}

function toNullableNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function toDisplayText(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (value == null) return fallback;
  return String(value);
}

function resolvePickedVariant(product: RecommendedProduct) {
  const picked = Array.isArray(product.picked_variant_values)
    ? product.picked_variant_values
    : [];
  const combinations = Array.isArray(product.variant_combinations)
    ? product.variant_combinations
    : [];
  if (!picked.length || !combinations.length) return null;
  return (
    combinations.find(
      (vc) =>
        vc.values.length === picked.length &&
        vc.values.every((v, i) => v === picked[i]),
    ) ?? null
  );
}

interface GuideResultCardProps {
  product: RecommendedProduct;
  onBuy?: () => void;
}

export function GuideResultCard({ product, onBuy }: GuideResultCardProps) {
  const image = product.images?.[0]?.url;
  const pickedVariant = resolvePickedVariant(product);
  const price = toNullableNumber(product.price) ?? toNullableNumber(pickedVariant?.price) ?? null;
  const compareAtPrice =
    toNullableNumber(product.compare_at_price) ?? toNullableNumber(pickedVariant?.compare_at_price) ?? null;
  const hasDiscount = compareAtPrice != null && price != null && compareAtPrice > price;
  const productName = toDisplayText(product.name, "Product");
  const currencyDisplay = toDisplayText(product.currency_display);
  const variantDimensions = Array.isArray(product.variant_dimensions) ? product.variant_dimensions : [];
  const pickedVariantValues = Array.isArray(product.picked_variant_values) ? product.picked_variant_values : [];

  const pickedLabel = variantDimensions
    .map((dim, i) => {
      const value = pickedVariantValues[i];
      if (!value) return null;
      return `${dim.charAt(0).toUpperCase() + dim.slice(1)}: ${value}`;
    })
    .filter(Boolean)
    .join(', ');

  return (
    <div className="flex flex-col gap-3" style={{ fontFamily: 'var(--font-body)' }}>
      {image && (
        <img
          src={image}
          alt={productName}
          className="w-full rounded-lg object-cover"
          style={{ aspectRatio: '3 / 4', background: '#f1f5f9' }}
        />
      )}
      <h3 className="ww-type-heading-sm" style={{ color: 'var(--text-secondary)' }}>
        {productName}
      </h3>
      {price != null && (
        <div className="flex items-baseline gap-2">
          <span
            className="ww-type-body-md font-semibold"
            style={{ color: 'var(--text-secondary)' }}
          >
            {price.toLocaleString()} {currencyDisplay}
          </span>
          {hasDiscount && (
            <span
              className="ww-type-body-sm line-through"
              style={{ color: 'var(--text-tertiary)' }}
            >
              {compareAtPrice!.toLocaleString()} {currencyDisplay}
            </span>
          )}
        </div>
      )}
      {pickedLabel && (
        <span className="ww-type-body-sm" style={{ color: 'var(--text-tertiary)' }}>
          {pickedLabel}
        </span>
      )}
      {onBuy && (
        <button type="button" className="ww-btn ww-btn-primary mt-1" onClick={onBuy}>
          Läs mer
        </button>
      )}
    </div>
  );
}
